import { parseYAML } from 'utils/yaml';
import {
  EIRData,
  EIREntry,
  JournalNote,
  JournalExportNote,
  ChatJournalEntry,
  CalendarEvent,
  CalendarWeekday,
  CalendarRecurrence,
  NutritionLogEntry,
  HealthAction,
  HealthMemoryItem
} from 'types';
import { normalizeHealthMemoryItem } from 'utils/healthMemoryNormalization';

export const JOURNAL_STORAGE_KEY = 'eir-journal-notes';
export const CHAT_JOURNAL_STORAGE_KEY = 'eir-chat-journal';
export const EIR_FILE_STORAGE_KEY = 'eir-file-content';
export const EIR_DATA_STORAGE_KEY = 'eir-data-parsed';
export const HEALTH_ANALYSIS_STORAGE_KEY = 'eir-health-analysis';
export const HEALTH_ANALYSIS_META_STORAGE_KEY = 'eir-health-analysis-meta';
export const CALENDAR_EVENTS_STORAGE_KEY = 'eir-calendar-events';
export const CALENDAR_SUGGESTIONS_STORAGE_KEY = 'eir-calendar-suggestions';

export const clampScore = (value: number | undefined): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 50;
  }

  const rounded = Math.round(value);
  return Math.min(100, Math.max(0, rounded));
};

export const formatNoteTitle = (isoString: string): string => {
  const parsedDate = new Date(isoString);

  if (Number.isNaN(parsedDate.getTime())) {
    return 'Health journal entry';
  }

  try {
    const formatter = new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });

    return `Health journal · ${formatter.format(parsedDate)}`;
  } catch (error) {
    return `Health journal · ${parsedDate.toLocaleString()}`;
  }
};

export const mapLegacyMoodToScore = (mood?: string): number => {
  switch (mood) {
    case 'energized':
      return 90;
    case 'okay':
      return 70;
    case 'tired':
      return 40;
    case 'in-pain':
      return 20;
    case 'concerned':
      return 30;
    default:
      return 50;
  }
};

export const normalizeJournalNotes = (rawNotes: any[]): JournalNote[] =>
  rawNotes
    .filter((note) => note && typeof note.content === 'string' && note.content.trim().length > 0)
    .map((note, index) => {
      const createdAt = typeof note.createdAt === 'string'
        ? note.createdAt
        : typeof note.created_at === 'string'
          ? note.created_at
          : new Date().toISOString();

      const id = typeof note.id === 'string' && note.id.trim().length > 0
        ? note.id
        : `imported-note-${Date.now()}-${index}`;

      const wellbeingCandidate =
        typeof note.wellbeingScore === 'number'
          ? note.wellbeingScore
          : typeof note.wellbeing_score === 'number'
            ? note.wellbeing_score
            : typeof note.moodScore === 'number'
              ? note.moodScore
              : mapLegacyMoodToScore(note.mood);

      const title = typeof note.title === 'string' && note.title.trim().length > 0
        ? note.title
        : formatNoteTitle(createdAt);

      return {
        id,
        title,
        content: note.content,
        createdAt,
        wellbeingScore: clampScore(wellbeingCandidate),
        source: note.source === 'chat' ? 'chat' : 'manual'
      } as JournalNote;
    });

export const normalizeExportedNotes = (entries?: JournalExportNote[]): JournalNote[] =>
  Array.isArray(entries) ? normalizeJournalNotes(entries as any[]) : [];

export const normalizeChatJournalEntries = (rawEntries: any[]): ChatJournalEntry[] =>
  rawEntries
    .filter((entry) => entry && typeof entry.summary === 'string' && entry.summary.trim().length > 0)
    .map((entry, index) => {
      const timestamp = typeof entry.timestamp === 'string' && entry.timestamp.trim().length > 0
        ? entry.timestamp
        : new Date().toISOString();

      const id = typeof entry.id === 'string' && entry.id.trim().length > 0
        ? entry.id
        : `chat-journal-${Date.now()}-${index}`;

      return {
        id,
        summary: entry.summary.trim(),
        timestamp,
        // Preserve transcript and aiDescription if they exist
        transcript: typeof entry.transcript === 'string' ? entry.transcript : undefined,
        aiDescription: typeof entry.aiDescription === 'string' ? entry.aiDescription : undefined
      };
    });

export const CALENDAR_WEEKDAYS: CalendarWeekday[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

export const normalizeCalendarEvents = (rawEvents: any[]): CalendarEvent[] =>
  rawEvents
    .filter((event) => event && typeof event.title === 'string' && event.title.trim().length > 0)
    .map((event, index) => {
      const now = new Date();
      const fallbackStart = now.toISOString();
      const fallbackEnd = new Date(now.getTime() + 60 * 60 * 1000).toISOString();

      const normalizeIso = (value: unknown, fallback: string): string => {
        if (typeof value === 'string') {
          const parsed = new Date(value);
          if (!Number.isNaN(parsed.getTime())) {
            return parsed.toISOString();
          }
        }
        return fallback;
      };

      const start = normalizeIso(event.start, fallbackStart);
      const endCandidate = normalizeIso(event.end, fallbackEnd);
      const end = new Date(endCandidate).getTime() > new Date(start).getTime()
        ? endCandidate
        : new Date(new Date(start).getTime() + 30 * 60 * 1000).toISOString();

      const normalizeRecurrence = (): CalendarRecurrence | undefined => {
        if (!event.recurrence || typeof event.recurrence !== 'object') {
          return undefined;
        }

        const frequency = typeof event.recurrence.frequency === 'string'
          ? event.recurrence.frequency.toUpperCase()
          : '';

        if (!['DAILY', 'WEEKLY', 'MONTHLY'].includes(frequency)) {
          return undefined;
        }

        const recurrence: CalendarRecurrence = {
          frequency: frequency as CalendarRecurrence['frequency']
        };

        if (typeof event.recurrence.interval === 'number' && event.recurrence.interval > 0) {
          recurrence.interval = event.recurrence.interval;
        }

        if (typeof event.recurrence.count === 'number' && event.recurrence.count > 0) {
          recurrence.count = event.recurrence.count;
        }

        if (typeof event.recurrence.endDate === 'string') {
          const normalizedEnd = normalizeIso(event.recurrence.endDate, '');
          if (normalizedEnd) {
            recurrence.endDate = normalizedEnd;
          }
        }

        if (Array.isArray(event.recurrence.weekdays)) {
          const normalizedWeekdays = event.recurrence.weekdays
            .map((weekday: unknown) => (typeof weekday === 'string' ? weekday.toUpperCase() : ''))
            .filter((weekday: string): weekday is CalendarWeekday => CALENDAR_WEEKDAYS.includes(weekday as CalendarWeekday));

          if (normalizedWeekdays.length > 0) {
            recurrence.weekdays = normalizedWeekdays;
          }
        }

        return recurrence;
      };

      const createdAt = normalizeIso(event.createdAt, start);
      const updatedAt = normalizeIso(event.updatedAt, end);

      return {
        id: typeof event.id === 'string' && event.id.trim().length > 0
          ? event.id
          : `calendar-${Date.now()}-${index}`,
        title: event.title.trim(),
        description: typeof event.description === 'string' ? event.description : undefined,
        start,
        end,
        allDay: Boolean(event.allDay),
        recurrence: normalizeRecurrence(),
        source: event.source === 'ai' ? 'ai' : 'manual',
        createdAt,
        updatedAt
      } as CalendarEvent;
    });

export const parseEirText = (text: string): {
  data: EIRData; 
  notes: JournalNote[];
  chatEntries?: ChatJournalEntry[];
  healthAssessments?: { responses?: Record<string, Record<string, number>>; results?: Record<string, any> };
  nutritionEntries?: NutritionLogEntry[];
  healthActions?: HealthAction[];
  healthMemoryItems?: HealthMemoryItem[];
} => {
  let parsed: any = null;

  try {
    const candidate = JSON.parse(text);
    if (candidate && typeof candidate === 'object' && Array.isArray(candidate.entries)) {
      parsed = candidate;
    }
  } catch (jsonError) {
    // Not valid JSON, will attempt YAML parsing below.
  }

  if (!parsed) {
    const yamlData = parseYAML(text);
    if (yamlData && typeof yamlData === 'object') {
      parsed = yamlData;
    }
  }

  if (!parsed || !Array.isArray(parsed.entries)) {
    throw new Error('Invalid EIR data structure');
  }

  const metadata = parsed.metadata ?? {};
  const entries = parsed.entries as EIREntry[];
  const notes = normalizeExportedNotes(parsed.journal_entries);

  // Extract extended format entries (backward compatible - these are optional)
  const chatEntries = Array.isArray(parsed.chat_entries) ? parsed.chat_entries as ChatJournalEntry[] : undefined;
  const healthAssessments = parsed.health_assessments ? {
    responses: parsed.health_assessments.responses && typeof parsed.health_assessments.responses === 'object' 
      ? parsed.health_assessments.responses 
      : undefined,
    results: parsed.health_assessments.results && typeof parsed.health_assessments.results === 'object'
      ? parsed.health_assessments.results
      : undefined
  } : undefined;
  
  // Debug logging for health assessments parsing
  if (parsed.health_assessments) {
    console.log('[parseEirText] Found health_assessments in parsed data:', {
      hasResponses: !!parsed.health_assessments.responses,
      hasResults: !!parsed.health_assessments.results,
      responsesKeys: parsed.health_assessments.responses ? Object.keys(parsed.health_assessments.responses) : [],
      resultsKeys: parsed.health_assessments.results ? Object.keys(parsed.health_assessments.results) : []
    });
    console.log('[parseEirText] Extracted healthAssessments object:', {
      hasResponses: !!healthAssessments?.responses,
      hasResults: !!healthAssessments?.results,
      isUndefined: healthAssessments === undefined,
      responsesKeys: healthAssessments?.responses ? Object.keys(healthAssessments.responses) : [],
      resultsKeys: healthAssessments?.results ? Object.keys(healthAssessments.results) : []
    });
  }
  const nutritionEntries = Array.isArray(parsed.nutrition_entries) ? parsed.nutrition_entries as NutritionLogEntry[] : undefined;
  const healthActions = Array.isArray(parsed.health_actions) ? parsed.health_actions as HealthAction[] : undefined;

  const rawHealthMemory = Array.isArray(parsed.health_memory_items) ? parsed.health_memory_items : undefined;
  const healthMemoryItems = rawHealthMemory
    ? rawHealthMemory.map(normalizeHealthMemoryItem).filter((item): item is HealthMemoryItem => item !== null)
    : undefined;

  return {
    data: {
      metadata,
      entries,
      // Include extended entries in data object for export
      chat_entries: chatEntries,
      health_assessments: healthAssessments,
      nutrition_entries: nutritionEntries,
      health_actions: healthActions,
      health_memory_items: healthMemoryItems,
    },
    notes,
    chatEntries,
    healthAssessments,
    nutritionEntries,
    healthActions,
    healthMemoryItems,
  };
};
